const REAIS = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const NUMERO = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 });

export function reais(valor: number | null | undefined): string {
  return valor === null || valor === undefined ? '—' : REAIS.format(valor);
}

/**
 * Percentual sempre com 2 casas. É o mesmo arredondamento que o motor usa para
 * classificar a faixa — exibir 76% para uma taxa de 75,99% foi um dos
 * problemas apontados na auditoria da planilha.
 */
export function percentual(taxa: number | null | undefined): string {
  return taxa === null || taxa === undefined ? '—' : `${(taxa * 100).toFixed(2).replace('.', ',')}%`;
}

export function numero(valor: number | null | undefined): string {
  return valor === null || valor === undefined ? '—' : NUMERO.format(valor);
}

export function data(iso: string | null | undefined): string {
  if (!iso) return '—';
  const [ano, mes, dia] = iso.slice(0, 10).split('-');
  return dia && mes && ano ? `${dia}/${mes}/${ano}` : iso;
}

/** Primeiro e último dia do mês anterior — o período usual de fechamento. */
export function mesAnterior(): { inicio: string; fim: string } {
  const hoje = new Date();
  const primeiroDesteMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
  const ultimoDoAnterior = new Date(primeiroDesteMes.getTime() - 86_400_000);
  const primeiroDoAnterior = new Date(ultimoDoAnterior.getFullYear(), ultimoDoAnterior.getMonth(), 1);
  const iso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { inicio: iso(primeiroDoAnterior), fim: iso(ultimoDoAnterior) };
}

export const CARGOS: Record<string, string> = {
  atendente: 'Atendente',
  supervisor: 'Supervisor',
  backoffice: 'Backoffice',
};

/**
 * Duração em texto curto: "2h 14min", "38min", "45s".
 *
 * Sem casas decimais e sem zeros à esquerda porque quem lê a tela de sessões
 * quer a ordem de grandeza — "ficou umas duas horas" — e não a precisão.
 */
export function duracao(segundos: number): string {
  if (!Number.isFinite(segundos) || segundos < 0) return '—';
  if (segundos < 60) return `${Math.round(segundos)}s`;

  const minutos = Math.floor(segundos / 60);
  if (minutos < 60) return `${minutos}min`;

  const horas = Math.floor(minutos / 60);
  const resto = minutos % 60;
  if (horas < 24) return resto === 0 ? `${horas}h` : `${horas}h ${resto}min`;

  const dias = Math.floor(horas / 24);
  const horasRestantes = horas % 24;
  return horasRestantes === 0 ? `${dias}d` : `${dias}d ${horasRestantes}h`;
}

/** "há 3min", "há 2h", "agora" — para a última atividade de uma sessão. */
export function desde(iso: string | null | undefined): string {
  if (!iso) return '—';
  const quando = new Date(iso).getTime();
  if (!Number.isFinite(quando)) return '—';

  const segundos = Math.max(0, (Date.now() - quando) / 1000);
  if (segundos < 45) return 'agora';
  return `há ${duracao(segundos)}`;
}

/** Data e hora, para a tela de sessões: "31/07 14:32". */
export function dataHora(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/**
 * A competência que está sendo apurada agora: o mês anterior ao de hoje.
 *
 * O fechamento sempre roda sobre o mês passado — nunca sobre o mês corrente,
 * que ainda não terminou. É o padrão certo para qualquer data de vigência que
 * a pessoa vá digitar.
 */
export function primeiroDiaDaCompetenciaAtual(): string {
  const hoje = new Date();
  const anterior = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
  const mes = String(anterior.getMonth() + 1).padStart(2, '0');
  return `${anterior.getFullYear()}-${mes}-01`;
}

const MESES = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
];

/**
 * A data em linguagem de competência.
 *
 * Primeiro dia do mês vira "setembro/2026"; qualquer outro dia continua em
 * data, porque aí a vigência de fato começa no meio do mês e arredondar para
 * o mês seria mentir.
 */
export function competencia(iso: string | null | undefined): string {
  if (!iso) return '—';
  const [ano, mes, dia] = iso.slice(0, 10).split('-');
  if (ano === undefined || mes === undefined || dia === undefined) return String(iso);
  const nome = MESES[Number(mes) - 1];
  if (dia !== '01' || nome === undefined) return `${dia}/${mes}/${ano}`;
  return `${nome}/${ano}`;
}
